import { Input } from "@/components/ui/input";
import { getPostKicker } from "@/utils/postKicker";
import BlogCard from "@/components/BlogCard";

import { blogPosts } from "@/data/blogPosts";
import { categories } from "@/data/categories";
import { Search, Bot, Info, ChevronRight, ArrowRight, Wifi } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";

const Blog = () => {
  useSEO({
    title: "News IA para Pymes | Soluciones probadas - Globaltis IA",
    description:
      "Descubre soluciones de IA probadas para tu negocio. Artículos, tutoriales y guías prácticas sobre automatización, agentes de voz y herramientas de IA.",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [isAISearching, setIsAISearching] = useState(false);
  const [aiResults, setAiResults] = useState<
    Array<{ id: string; relevance: number; reason: string }>
  >([]);
  const [searchMode, setSearchMode] = useState<"ai" | "fallback">("ai");

  // AI search with debounce
  useEffect(() => {
    const performAISearch = async () => {
      if (!searchTerm || searchTerm.length < 3) {
        setAiResults([]);
        setSearchMode("ai");
        return;
      }

      setIsAISearching(true);
      try {
        const { data: functionData, error: functionError } =
          await supabase.functions.invoke("semantic-search", {
            body: {
              query: searchTerm,
              articles: blogPosts.map((p) => ({
                id: p.id,
                title: p.title,
                description: p.description,
                insight: p.insight,
                category: p.category,
                content: p.content,
              })),
            },
          });

        if (functionError) throw functionError;

        if (
          functionData.error ||
          !functionData.results ||
          functionData.results.length === 0
        ) {
          setSearchMode("fallback");
          setAiResults([]);
        } else {
          setSearchMode("ai");
          const uniqueResults = Object.values(
            functionData.results.reduce((acc: Record<string, any>, r: any) => {
              if (!acc[r.id] || r.relevance > acc[r.id].relevance) {
                acc[r.id] = r;
              }
              return acc;
            }, {})
          );
          setAiResults(
            uniqueResults as Array<{
              id: string;
              relevance: number;
              reason: string;
            }>
          );
        }
      } catch (error: any) {
        setSearchMode("fallback");
        setAiResults([]);

        if (error.message?.includes("429")) {
          toast.error("Límite de búsquedas alcanzado. Usando búsqueda tradicional.");
        } else if (error.message?.includes("402")) {
          toast.error("Créditos de IA agotados. Usando búsqueda tradicional.");
        }
      } finally {
        setIsAISearching(false);
      }
    };

    const timer = setTimeout(performAISearch, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get unique categories from actual posts
  const postCategories = useMemo(() => {
    const cats = new Set(blogPosts.map((p) => p.category));
    return Array.from(cats).sort();
  }, []);

  // Hybrid filtering (search + section)
  const filteredPosts = useMemo(() => {
    let base = blogPosts;

    // Filter by section first
    if (activeSection) {
      base = base.filter((p) => p.category === activeSection);
    }

    // Then filter by search
    base = base.filter((post) => {
      if (!searchTerm) return true;

      if (searchMode === "ai" && aiResults.length > 0) {
        return aiResults.some((r) => r.id === post.id);
      } else {
        const s = searchTerm.toLowerCase();
        return (
          post.title.toLowerCase().includes(s) ||
          post.description.toLowerCase().includes(s) ||
          post.insight.toLowerCase().includes(s) ||
          (post.content && post.content.toLowerCase().includes(s))
        );
      }
    });

    if (searchMode === "ai" && aiResults.length > 0) {
      return [...base].sort((a, b) => {
        const aR = aiResults.find((r) => r.id === a.id)?.relevance || 0;
        const bR = aiResults.find((r) => r.id === b.id)?.relevance || 0;
        return bR - aR;
      });
    }

    return [...base].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [searchTerm, searchMode, aiResults, activeSection]);

  // Editorial front page: group posts by category sections
  const sortedByDate = useMemo(
    () =>
      [...blogPosts].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    []
  );

  const isPortada = !searchTerm && !activeSection;

  // Hero block: 1 center + 3 left + 3 right = 7 posts
  const HERO_CENTER_SLUG = "chatgpt-images-2-catalogos-producto-pymes";
  const heroPosts = useMemo(() => {
    const all = [...sortedByDate];
    const pinnedIdx = all.findIndex(p => p.slug === HERO_CENTER_SLUG);
    const pinned = pinnedIdx >= 0 ? all.splice(pinnedIdx, 1)[0] : all.shift()!;
    // Count articles per category to protect categories from hero absorption
    const catCount = new Map<string, number>();
    for (const p of sortedByDate) catCount.set(p.category, (catCount.get(p.category) || 0) + 1);
    // Reserve enough articles per category for section blocks (1 featured + up to 3 rest = 4)
    // Hero may take at most: total - 4 from each category (min 0)
    const heroQuotaPerCat = new Map<string, number>();
    for (const [cat, count] of catCount) {
      heroQuotaPerCat.set(cat, Math.max(0, count - 4));
    }
    // Also avoid taking the sole representative of a category
    const usedPerCat = new Map<string, number>();
    const top6: typeof all = [];
    // Prefer posts from categories with surplus; fall back to anything if needed
    const tryPick = (predicate: (p: typeof all[0]) => boolean) => {
      for (const p of all) {
        if (top6.length >= 6) break;
        if (top6.includes(p)) continue;
        if (!predicate(p)) continue;
        const used = usedPerCat.get(p.category) || 0;
        const quota = heroQuotaPerCat.get(p.category) ?? 0;
        if (used >= quota) continue;
        top6.push(p);
        usedPerCat.set(p.category, used + 1);
      }
    };
    tryPick(() => true);
    // If we still don't have 6, relax the quota (allow taking from categories with >1 article)
    if (top6.length < 6) {
      for (const p of all) {
        if (top6.length >= 6) break;
        if (top6.includes(p)) continue;
        if ((catCount.get(p.category) || 0) <= 1) continue;
        top6.push(p);
      }
    }
    // Last resort: fill from anything
    if (top6.length < 6) {
      for (const p of all) {
        if (top6.length >= 6) break;
        if (top6.includes(p)) continue;
        top6.push(p);
      }
    }
    // Sort side posts so same categories are adjacent
    top6.sort((a, b) => a.category.localeCompare(b.category) || new Date(b.date).getTime() - new Date(a.date).getTime());
    return [pinned, ...top6];
  }, [sortedByDate]);
  const heroCenter = heroPosts[0];
  const heroLeft = heroPosts.slice(1, 4);
  const heroRight = heroPosts.slice(4, 7);

  // Group posts by category preserving order
  const groupByCategory = (posts: typeof blogPosts) => {
    const groups: Array<{ category: string; posts: typeof blogPosts }> = [];
    for (const post of posts) {
      const last = groups[groups.length - 1];
      if (last && last.category === post.category) {
        last.posts.push(post);
      } else {
        groups.push({ category: post.category, posts: [post] });
      }
    }
    return groups;
  };

  // Hero post IDs to exclude from section blocks
  const heroPostIds = useMemo(() => new Set(heroPosts.map((p) => p.id)), [heroPosts]);

  // Group remaining posts by category for section blocks
  const sectionBlocks = useMemo(() => {
    if (!isPortada) return [];
    const remaining = sortedByDate.filter((p) => !heroPostIds.has(p.id));
    const catMap = new Map<string, typeof blogPosts>();
    for (const post of remaining) {
      if (!catMap.has(post.category)) catMap.set(post.category, []);
      catMap.get(post.category)!.push(post);
    }
    // Priority order: most relevant categories first
    const categoryPriority: Record<string, number> = {
      "Automatización": 1,
      "Productividad": 2,
      "Ciberseguridad": 3,
      "Estrategia IA": 4,
      "Desarrollo": 5,
      "Herramientas IA": 6,
      "El Rincón del Emprendedor": 7,
      "Sobre Globaltis": 8,
    };
    return Array.from(catMap.entries())
      .filter(([, posts]) => posts.length > 0)
      .sort(([a], [b]) => (categoryPriority[a] ?? 99) - (categoryPriority[b] ?? 99))
      .map(([category, posts]) => ({
        category,
        featured: posts[0],
        rest: posts.slice(1, 4),
      }));
  }, [isPortada, sortedByDate, heroPostIds]);

  const remainingPosts = useMemo(
    () => (isPortada ? [] : filteredPosts),
    [isPortada, filteredPosts]
  );

  const truncate = (text: string, max = 360) =>
    text.length > max ? text.slice(0, max) + "…" : text;

  const getRelativeDate = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "hoy";
    if (diffDays === 1) return "ayer";
    if (diffDays < 7) return `hace ${diffDays}d`;
    if (diffDays < 30) return `hace ${Math.floor(diffDays / 7)}sem`;
    return `hace ${Math.floor(diffDays / 30)}m`;
  };

  const heroReveal = useScrollReveal(0.1);
  const featuredReveal = useScrollReveal(0.01);
  
  const searchReveal = useScrollReveal(0.2);
  const gridReveal = useScrollReveal(0.05);
  const ctaReveal = useScrollReveal(0.2);

  const revealClass = (isVisible: boolean, delay = "") =>
    `transition-all duration-700 ${delay} ${
      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  const today = new Date().toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Newspaper Masthead */}
      <section
        ref={heroReveal.ref}
        className={`pt-12 md:pt-16 pb-6 md:pb-8 px-4 ${revealClass(heroReveal.isVisible)}`}
      >
        <div className="max-w-6xl mx-auto relative">
          {/* Live signal */}
          <div className="absolute right-0 top-0 flex items-center gap-1.5">
            <span className="text-[10px] tracking-[0.15em] uppercase text-primary/40 font-medium hidden sm:inline">En directo</span>
            <Wifi className="w-3.5 h-3.5 text-primary animate-pulse" />
          </div>

          {/* Date line */}
          <p className="text-xs tracking-widest uppercase text-muted-foreground/50 text-center mb-6">
            {today}
          </p>

          {/* Masthead */}
          <div className="text-center space-y-3">
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-extralight text-foreground tracking-tight leading-none">
              Descubre qué IA ya funciona para tu negocio
            </h1>
            <p className="text-sm md:text-base text-muted-foreground/60 font-light tracking-wide">
              Sin teorías · Solo soluciones probadas con resultados reales
            </p>
          </div>
        </div>
      </section>

      {/* Double rule — newspaper style */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="border-t-2 border-foreground/80" />
        <div className="border-t border-foreground/20 mt-[2px]" />
      </div>

      {/* Section Nav — newspaper sections */}
      <div className="relative max-w-6xl mx-auto px-4">
        {/* Left fade + chevron */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background via-background/60 to-transparent z-10 pointer-events-none flex items-center pl-2">
          <ChevronRight className="w-4 h-4 text-muted-foreground/60 rotate-180" />
        </div>
        {/* Right fade + chevron */}
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background via-background/60 to-transparent z-10 pointer-events-none flex items-center justify-end pr-2">
          <ChevronRight className="w-4 h-4 text-muted-foreground/60 animate-[pulse_2s_ease-in-out_infinite]" />
        </div>
        <nav className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-0 pr-8 min-w-max">
            <button
              onClick={() => setActiveSection(null)}
              className={`relative px-3 md:px-4 py-3 text-[11px] tracking-[0.15em] md:tracking-[0.2em] uppercase whitespace-nowrap transition-all duration-300 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-foreground after:transition-all after:duration-500 after:ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                !activeSection
                  ? "text-foreground font-medium after:w-full"
                  : "text-muted-foreground/50 hover:text-foreground/70 after:w-0 hover:after:w-1/2 hover:after:bg-foreground/30"
              }`}
            >
              Portada
            </button>
            {postCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveSection(activeSection === cat ? null : cat)}
                className={`relative px-3 md:px-4 py-3 text-[11px] tracking-[0.15em] md:tracking-[0.2em] uppercase whitespace-nowrap transition-all duration-300 after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:bg-foreground after:transition-all after:duration-500 after:ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                  activeSection === cat
                    ? "text-foreground font-medium after:w-full"
                    : "text-muted-foreground/50 hover:text-foreground/70 after:w-0 hover:after:w-1/2 hover:after:bg-foreground/30"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Thin rule under nav */}
      <div className="max-w-6xl mx-auto px-4">
        <div className="border-t border-border/30" />
      </div>

      {/* Editorial Front Page */}
      {isPortada && (
        <section
          className="py-10 md:py-16 px-4"
        >
          <div className="max-w-6xl mx-auto">

            {/* ═══ HERO BLOCK: center image + flanking headlines ═══ */}
            {heroCenter && (
              <div className="grid grid-cols-1 lg:grid-cols-[3fr_5fr_3fr] gap-0 lg:divide-x lg:divide-border/30">
                {/* Left column — grouped by category */}
                <div className="hidden lg:flex flex-col pr-8">
                  {groupByCategory(heroLeft).map((group, gIdx) => (
                    <div key={group.category} className={gIdx > 0 ? "border-t border-border/20 pt-4 mt-1" : ""}>
                      {group.posts.length >= 2 && (
                        <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-2">
                          {group.category}
                        </p>
                      )}
                      {group.posts.map((post, i) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className={`group block py-3 ${i > 0 ? "border-t border-border/10" : ""}`}
                        >
                          {group.posts.length === 1 && (
                            <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-1">
                              {post.category}
                            </p>
                          )}
                          <h3 className={`${i === 0 ? "text-lg font-bold" : "text-base font-semibold"} text-foreground leading-snug mb-1.5 group-hover:text-primary transition-colors duration-500`}>
                            {post.title}
                          </h3>
                          <p className="text-xs text-foreground font-semibold leading-relaxed">
                            {truncate(post.description, 120)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Center — hero image + title */}
                <div className="lg:px-8">
                  <div className="overflow-hidden rounded-xl mb-5">
                    <div className="aspect-[4/3] w-full">
                      {heroCenter.videoUrl ? (() => {
                        const ytMatch = heroCenter.videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
                        if (ytMatch) {
                          return (
                            <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
                              <iframe
                                src={`https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&modestbranding=1&playlist=${ytMatch[1]}`}
                                title={heroCenter.title}
                                allow="autoplay; encrypted-media; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full border-0 pointer-events-none"
                              />
                            </div>
                          );
                        }
                        return (
                          <div
                            className="relative w-full h-full rounded-xl overflow-hidden"
                            style={{ backgroundImage: `url(${heroCenter.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                          >
                            <video
                              src={heroCenter.videoUrl}
                              autoPlay
                              muted
                              loop
                              playsInline
                              className="w-full h-full object-cover rounded-xl relative z-10"
                            />
                          </div>
                        );
                      })() : (
                        <Link to={`/blog/${heroCenter.slug}`} className="block group">
                          <img
                            src={heroCenter.image}
                            alt={heroCenter.title}
                            className="object-cover w-full h-full grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                          />
                        </Link>
                      )}
                    </div>
                  </div>
                  <Link to={`/blog/${heroCenter.slug}`} className="group block">
                    <p className="text-xs tracking-[0.2em] uppercase text-primary/50 font-semibold mb-2">
                      {heroCenter.category}
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-extralight text-foreground leading-tight tracking-tight mb-3 group-hover:text-primary transition-colors duration-500">
                      {heroCenter.title}
                    </h2>
                    <p className="text-sm md:text-base text-foreground font-semibold leading-relaxed">
                      {truncate(heroCenter.description, 360)}
                    </p>
                  </Link>
                </div>

                {/* Right column — grouped by category */}
                <div className="hidden lg:flex flex-col pl-8">
                  {groupByCategory(heroRight).map((group, gIdx) => (
                    <div key={group.category} className={gIdx > 0 ? "border-t border-border/20 pt-4 mt-1" : ""}>
                      {group.posts.length >= 2 && (
                        <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-2">
                          {group.category}
                        </p>
                      )}
                      {group.posts.map((post, i) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className={`group block py-3 ${i > 0 ? "border-t border-border/10" : ""}`}
                        >
                          {group.posts.length === 1 && (
                            <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-1">
                              {post.category}
                            </p>
                          )}
                          <h3 className={`${i === 0 ? "text-lg font-bold" : "text-base font-semibold"} text-foreground leading-snug mb-1.5 group-hover:text-primary transition-colors duration-500`}>
                            {post.title}
                          </h3>
                          <p className="text-xs text-foreground font-semibold leading-relaxed">
                            {truncate(post.description, 120)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mobile: show flanking posts below hero */}
            <div className="lg:hidden mt-8 space-y-0">
              {[...heroLeft, ...heroRight].map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`group block py-4 ${i > 0 ? "border-t border-border/20" : ""}`}
                >
                  <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-1">
                    {post.category}
                  </p>
                  <h3 className="text-base font-semibold text-foreground leading-snug mb-1 group-hover:text-primary transition-colors duration-500">
                    {post.title}
                  </h3>
                  <p className="text-xs text-foreground font-semibold">
                    {truncate(post.description)}
                  </p>
                </Link>
              ))}
            </div>

            {/* ═══ GUIDES BANNER — Apple style ═══ */}
            <div className="my-3 md:my-4">
              <div className="border-t border-border/30 mb-3" />
              <Link to="/cursos" className="group block">
                <div className="bg-muted/30 rounded-xl px-5 py-4 md:px-8 md:py-5 flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-6 transition-colors duration-500 hover:bg-muted/50">
                  <div className="flex-1 text-center md:text-left space-y-3">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold">
                      Guías prácticas
                    </p>
                    <h3 className="text-2xl md:text-3xl font-extralight text-foreground leading-tight tracking-tight">
                      Aprende a implementar IA en tu negocio.{" "}
                      <span className="text-muted-foreground/50">Paso a paso, sin tecnicismos.</span>
                    </h3>
                    <p className="text-sm text-muted-foreground/50 font-light max-w-xl">
                      Itinerarios diseñados para que cualquier pyme pueda automatizar procesos, crear contenido y atender clientes con IA — por su cuenta o con nuestra ayuda.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-primary/70 group-hover:text-primary transition-colors duration-500 shrink-0 md:self-center">
                    <span className="text-sm font-medium">Explorar guías</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </div>

            {/* ═══ SECTION BLOCKS: alternating layouts ═══ */}
            {sectionBlocks.map((section, sIdx) => (
              <div key={section.category}>
                {/* Separator */}
                <div className="border-t border-border/50 my-10 md:my-14" />

                {/* Section header */}
                <h2 className="text-xs sm:text-sm tracking-[0.2em] uppercase text-foreground font-bold mb-6 md:mb-8">
                  {section.category}
                </h2>

                {/* Alternating layout — preserve 5fr/3fr structure even with no secondary articles */}
                {(() => {
                  const hasRest = section.rest.length > 0;
                  const gridCols = sIdx % 2 === 0
                    ? "lg:grid-cols-[5fr_3fr]"
                    : "lg:grid-cols-[3fr_5fr]";
                  return (
                <div className={`grid grid-cols-1 ${gridCols} gap-0 lg:divide-x lg:divide-border/30`}>
                  {/* Featured article with image */}
                  <div className={`pb-8 lg:pb-0 ${sIdx % 2 === 0 ? "lg:pr-10 order-1" : "lg:pl-10 lg:order-2 order-1"}`}>
                    <Link to={`/blog/${section.featured.slug}`} className="group block">
                      <div className="overflow-hidden rounded-xl mb-5">
                        <div className="aspect-[2/1] w-full">
                          <img
                            src={section.featured.image}
                            alt={section.featured.title}
                            className="object-cover object-top w-full h-full grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                          />
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-2">
                          {getPostKicker(section.featured)}
                        </p>
                        <h3 className="text-xl md:text-2xl lg:text-3xl font-light text-foreground leading-tight tracking-tight mb-3 group-hover:text-primary transition-colors duration-500">
                          {section.featured.title}
                        </h3>
                        <p className="text-sm md:text-base text-foreground font-semibold leading-relaxed">
                          {truncate(section.featured.description, 360)}
                        </p>
                      </div>
                    </Link>
                  </div>

                  {/* Secondary column — always rendered; placeholder when empty */}
                  <div className={`flex flex-col ${sIdx % 2 === 0 ? "lg:pl-10 order-2" : "lg:pr-10 lg:order-1 order-2"}`}>
                    {hasRest ? (
                      <>
                        {section.rest.map((post, i) => (
                          <Link
                            key={post.id}
                            to={`/blog/${post.slug}`}
                            className={`group block flex-1 py-4 first:pt-0 ${i > 0 ? "border-t border-border/20" : ""}`}
                          >
                            <p className="text-[9px] tracking-[0.2em] uppercase text-primary/40 font-medium mb-1">
                              {getPostKicker(post)}
                            </p>
                            <h4 className="text-sm md:text-base font-semibold text-foreground leading-snug mb-1.5 group-hover:text-primary transition-colors duration-500">
                              {post.title}
                            </h4>
                            <p className="text-[13px] text-foreground font-semibold leading-relaxed">
                              {truncate(post.description)}
                            </p>
                          </Link>
                        ))}
                        <button
                          onClick={() => setActiveSection(section.category)}
                          className="mt-auto pt-4 text-[11px] tracking-[0.15em] uppercase text-primary/60 hover:text-primary transition-colors duration-300 text-left"
                        >
                          Ver más en {section.category} →
                        </button>
                      </>
                    ) : (
                      <div className="hidden lg:flex flex-1 items-center justify-center text-center px-4">
                        <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground/40 font-medium leading-relaxed">
                          Próximamente<br />más en<br />{section.category}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                  );
                })()}
              </div>
            ))}

          </div>
        </section>
      )}

      <div className="max-w-6xl mx-auto px-4">
        <div className="border-t border-border/30" />
      </div>

      {/* Search — minimal, integrated */}
      <section
        ref={searchReveal.ref}
        className={`py-8 md:py-10 px-4 ${revealClass(searchReveal.isVisible)}`}
      >
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Input
              type="text"
              placeholder="Buscar artículos…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-3 text-sm bg-transparent border-0 border-b border-border/30 rounded-none focus:border-foreground focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/40 transition-colors"
            />
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-muted-foreground/30 w-4 h-4" />
            {isAISearching && (
              <Bot className="absolute right-0 top-1/2 -translate-y-1/2 animate-pulse text-primary w-4 h-4" />
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <button className="p-2 text-muted-foreground/30 hover:text-primary transition-colors">
                <Info className="w-4 h-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4" align="end">
              <div className="space-y-2">
                <h3 className="font-medium text-xs flex items-center gap-2 text-foreground/80">
                  <Bot className="w-3.5 h-3.5 text-primary" />
                  Búsqueda inteligente
                </h3>
                <div className="space-y-1.5 text-[11px] text-muted-foreground">
                  <p>Negocio, problema, resultado o herramienta</p>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {searchMode === "ai" && aiResults.length > 0 && (
            <span className="text-[11px] text-muted-foreground/50 tracking-wide">
              {aiResults.length} resultados IA
            </span>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        <div className="border-t border-border/30" />
      </div>

      {/* Article Grid / Category Editorial View */}
      <section
        ref={gridReveal.ref}
        className={`py-10 md:py-16 px-4 ${revealClass(gridReveal.isVisible, "delay-75")}`}
      >
        <div className="max-w-6xl mx-auto">
          {/* Category editorial layout — mirrors portada structure */}
          {activeSection && !searchTerm ? (() => {
            const catPosts = [...blogPosts]
              .filter(p => p.category === activeSection)
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            
            if (catPosts.length === 0) return (
              <div className="text-center py-16">
                <p className="text-lg text-muted-foreground font-light">No se encontraron artículos</p>
              </div>
            );

            const featured = catPosts[0];
            const secondary = catPosts.slice(1, 4);
            const remaining = catPosts.slice(4);

            return (
              <>
                {/* Section header */}
                <h2 className="text-xs sm:text-sm tracking-[0.2em] uppercase text-foreground font-bold mb-6 md:mb-8">
                  {activeSection} · {catPosts.length} artículos
                </h2>

                {/* Featured + secondary — same structure as portada section blocks */}
                <div className="grid grid-cols-1 lg:grid-cols-[5fr_3fr] gap-0 lg:divide-x lg:divide-border/30">
                  {/* Featured article with image */}
                  <div className="pb-8 lg:pb-0 lg:pr-10">
                    <Link to={`/blog/${featured.slug}`} className="group block">
                      <div className="overflow-hidden rounded-xl mb-5">
                        <div className="aspect-[2/1] w-full">
                          <img
                            src={featured.image}
                            alt={featured.title}
                            className="object-cover w-full h-full grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-primary/50 font-semibold mb-2">
                        {getPostKicker(featured)}
                      </p>
                      <h3 className="text-xl md:text-2xl lg:text-3xl font-light text-foreground leading-tight tracking-tight mb-3 group-hover:text-primary transition-colors duration-500">
                        {featured.title}
                      </h3>
                      <p className="text-sm md:text-base text-foreground font-semibold leading-relaxed">
                        {truncate(featured.description, 360)}
                      </p>
                    </Link>
                  </div>

                  {/* Secondary articles — text only */}
                  {secondary.length > 0 && (
                    <div className="flex flex-col lg:pl-10">
                      {secondary.map((post, i) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className={`group block flex-1 py-4 first:pt-0 ${i > 0 ? "border-t border-border/20" : ""}`}
                        >
                          <p className="text-[9px] tracking-[0.2em] uppercase text-primary/40 font-medium mb-1">
                            {getPostKicker(post)}
                          </p>
                          <h4 className="text-sm md:text-base font-semibold text-foreground leading-snug mb-1.5 group-hover:text-primary transition-colors duration-500">
                            {post.title}
                          </h4>
                          <p className="text-[13px] text-foreground font-semibold leading-relaxed">
                            {truncate(post.description)}
                          </p>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Remaining articles — grid */}
                {remaining.length > 0 && (
                  <>
                    <div className="border-t border-border/30 my-10 md:my-14" />
                    <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground/40 mb-10">
                      Más en {activeSection}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                      {remaining.map((post) => (
                        <BlogCard key={post.id} post={post} />
                      ))}
                    </div>
                  </>
                )}
              </>
            );
          })() : (
            /* Search results or empty state */
            remainingPosts.length === 0 && filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-lg text-muted-foreground font-light">
                  No se encontraron artículos
                </p>
              </div>
            ) : remainingPosts.length === 0 && !searchTerm && !activeSection ? null : (
              <>
                <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground/40 mb-10">
                  {searchTerm
                    ? `${remainingPosts.length} ${remainingPosts.length === 1 ? "resultado" : "resultados"}`
                    : "Más artículos"}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {remainingPosts.map((post, index) => {
                    const aiReason = aiResults.find((r) => r.id === post.id);
                    return (
                      <div
                        key={post.id}
                        className={`transition-all duration-700 ${
                          gridReveal.isVisible
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-6"
                        }`}
                        style={{
                          transitionDelay: gridReveal.isVisible
                            ? `${Math.min(index * 80, 500)}ms`
                            : "0ms",
                        }}
                      >
                        <BlogCard
                          post={post}
                          aiReason={
                            searchMode === "ai" && aiResults.length > 0
                              ? aiReason?.reason || "Resultado relevante"
                              : undefined
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        <div className="border-t border-border/30" />
      </div>

      {/* CTA — minimal */}
      <section
        ref={ctaReveal.ref}
        className={`py-14 md:py-20 px-4 ${revealClass(ctaReveal.isVisible)}`}
      >
        <div className="max-w-3xl mx-auto text-center space-y-5">
          <h2 className="text-3xl md:text-4xl font-extralight text-foreground">
            ¿Quieres implementar estas ideas?
          </h2>
          <p className="text-sm text-muted-foreground/60 font-light">
            Convertimos artículos en soluciones reales con garantía.
          </p>
          <Link to="/">
            <Button
              variant="hero"
              size="lg"
              className="text-sm px-8 py-5 h-auto mt-4"
            >
              Hablemos de tu proyecto
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/** Reusable grayscale image with hover */
const AspectRatioImage = ({ src, alt }: { src: string; alt: string }) => (
  <div className="aspect-video w-full">
    <img
      src={src}
      alt={alt}
      className="object-cover w-full h-full grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
    />
  </div>
);

export default Blog;
